import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, effect, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { isBrownfieldTrigger, isResourceTrigger, isViolationTrigger } from 'src/app/common/playbookUtils';
import { PlaybookTrigger } from 'src/app/models/api/playbooks/playbook.model';
import { NewResourceFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newResourceTriggerModel';
import { NewViolationFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newViolationTriggerModel';
import { ViolationStatus } from 'src/app/models/api/violations.model';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { PlaybooksHttpServiceService } from 'src/app/services/httpServices/playbooksHttpService/playbooks-http-service.service';

@Component({
  selector: 'app-playbook-projection',
  templateUrl: './playbook-projection.component.html',
  styleUrls: ['./playbook-projection.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, LabelsProviderPipe, MatTooltipModule, MatProgressSpinnerModule],
})
export class PlaybookProjectionComponent {
  public integrationId = input<string>();
  public trigger = input<PlaybookTrigger>();
  public policyIds = input<string[]>();

  // Fields that make exploration unavailable for violation triggers
  private readonly VIOLATION_TRIGGER_BLOCKING_FIELDS = [
    'resourceSecurityRisk',
    'resourceBusinessCriticality',
    'resourceCreatedBy',
    'resourceLastModifiedBy',
    'resourceCreatedSince',
    'resourceCreatedUntil',
    'resourceFirstSeenSince',
    'resourceFirstSeenUntil',
    'labels',
  ];

  // Fields that make exploration unavailable for resource triggers
  private readonly RESOURCE_TRIGGER_BLOCKING_FIELDS = [
    'resourceCreatedBy',
    'resourceLastModifiedBy',
    'resourceFirstSeenSince',
    'resourceFirstSeenUntil',
  ];

  public hasAllRequiredInputs = computed(() => this.integrationId() && this.trigger() && this.policyIds()?.length);
  public isBrownfieldTrigger = computed(() => this.trigger() && isBrownfieldTrigger(this.trigger()!.triggerType));
  public isViolationTrigger = computed(() => this.trigger() && isViolationTrigger(this.trigger()!.triggerType));
  public isExplorationAvailable = computed(() => {
    const trigger = this.trigger()!;

    return isViolationTrigger(trigger.triggerType)
      ? !this.hasAnyBlockingFields(
          trigger.configuration as NewViolationFoundTriggerConfigModel,
          this.VIOLATION_TRIGGER_BLOCKING_FIELDS
        )
      : isResourceTrigger(trigger.triggerType)
        ? !this.hasAnyBlockingFields(
            trigger.configuration as NewResourceFoundTriggerConfigModel,
            this.RESOURCE_TRIGGER_BLOCKING_FIELDS
          )
        : true;
  });

  public projectionCount = signal<number | null>(null);
  public isLoading = signal(false);

  private hasAnyBlockingFields(
    configuration: NewViolationFoundTriggerConfigModel | NewResourceFoundTriggerConfigModel,
    fields: readonly string[]
  ): boolean {
    return fields.some((field) => (configuration as Record<string, unknown>)[field]);
  }

  constructor(
    private _router: Router,
    private _playbooksHttpService: PlaybooksHttpServiceService,
    private readonly _destroyRef: DestroyRef
  ) {
    effect(
      () => {
        const integrationId = this.integrationId();
        const trigger = this.trigger();
        const policyIds = this.policyIds();

        if (!integrationId || !trigger || !policyIds) {
          return;
        }

        this.isLoading.set(true);

        this._playbooksHttpService
          .getPlaybookProjection({
            integrationId,
            trigger,
            policyIds,
          })
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe({
            next: (response) => {
              this.projectionCount.set(response.total);
              this.isLoading.set(false);
            },
          });
      },
      { allowSignalWrites: true }
    );
  }

  public onClick(): void {
    const integrationId = this.integrationId();
    const policyIds = this.policyIds();
    const trigger = this.trigger();

    if (!integrationId || !policyIds || !trigger) {
      return;
    }

    const filters = isViolationTrigger(trigger.triggerType)
      ? this._convertToViolationFilters(
          integrationId,
          policyIds,
          trigger.configuration as NewViolationFoundTriggerConfigModel
        )
      : this._convertToResourceFilters(
          integrationId,
          policyIds,
          trigger.configuration as NewResourceFoundTriggerConfigModel
        );
    const filtersQuery = FiltersQueryTransformer.transformToQuery(filters);

    const url = this._router
      .createUrlTree(['app', isViolationTrigger(trigger.triggerType) ? 'violations' : 'inventory'], {
        queryParams: { q: filtersQuery },
      })
      .toString();

    window.open(url, '_blank');
  }

  private _convertToViolationFilters(
    integrationId: string,
    policyIds: string[],
    configuration: NewViolationFoundTriggerConfigModel
  ): ISelectedFilters {
    return {
      integration: [integrationId],
      policyId: policyIds,
      violationRule: configuration.ruleIds,
      severity: configuration.severity,
      type: configuration.resourceTypes,
      resourceStatus: configuration.resourceStatus,
      generativeAiOptions: configuration.resourceGenerativeAiOptions,
      ruleTag: configuration.ruleTags,
      violationStatus: [ViolationStatus.open],
    };
  }

  private _convertToResourceFilters(
    integrationId: string,
    policyIds: string[],
    configuration: NewResourceFoundTriggerConfigModel
  ): ISelectedFilters {
    return {
      integration: [integrationId],
      policyId: policyIds,
      type: configuration.resourceTypes,
      status: configuration.resourceStatus,
      fromDate: configuration.resourceCreatedSince,
      toDate: configuration.resourceCreatedUntil,
      securityRisk: configuration.resourceSecurityRisk,
      businessCriticality: configuration.resourceBusinessCriticality,
      generativeAiOptions: configuration.resourceGenerativeAiOptions,
    };
  }
}
